// src/components/WalletConnector.jsx

import React, { useState, useEffect, useRef } from 'react';
import { useWallet } from '../context/WalletContext';
import { 
  getWalletSupportInfo, 
  isSolanaWallet, 
  connectOkxSolana, 
  isOkxSolanaAvailable 
} from '../utils/walletUtils';
import { 
  getWalletIcon, 
  getWalletNetworks, 
  getNetworkSupportBadge,
  OkxInstallationGuide,
  OkxChainSelection
} from './WalletConnectorHelpers';
import '../styles/wallet-connector-updated.css';

/**
 * Enhanced Wallet Connector Modal
 * Supports multiple wallet types with improved mobile compatibility and OKX Solana support
 */
const WalletConnector = ({ isOpen, onClose, onSuccess }) => {
  const [walletType, setWalletType] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [networkFilter, setNetworkFilter] = useState('all');
  const [showChainSupportInfo, setShowChainSupportInfo] = useState(false);
  const [supportInfo, setSupportInfo] = useState({
    supportsEVM: false,
    supportsSolana: false,
    supportInfo: ""
  });
  const [chainPreference, setChainPreference] = useState(null);
  const [showOkxChainSelect, setShowOkxChainSelect] = useState(false);
  const [okxInstallationGuide, setOkxInstallationGuide] = useState(false);
  
  const { connectWallet, availableWallets, supportedNetworks } = useWallet();
  const modalRef = useRef(null);
  
  // Check if user is on mobile
  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      setIsMobile(/android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent.toLowerCase()));
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);
  
  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setWalletType(null);
      setIsConnecting(false);
      setError(null);
      setShowChainSupportInfo(false);
      setShowOkxChainSelect(false);
      setOkxInstallationGuide(false);
      setChainPreference(null);
      setSupportInfo({
        supportsEVM: false,
        supportsSolana: false,
        supportInfo: ""
      });
    }
  }, [isOpen]);
  
  // Handle clicks outside the modal to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target) && !isConnecting && !showOkxChainSelect && !okxInstallationGuide) {
        onClose();
      }
    };
    
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      // Prevent scrolling of background when modal is open
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = '';
    };
  }, [isOpen, isConnecting, showOkxChainSelect, okxInstallationGuide, onClose]);
  
  // Direct connection to OKX Solana wallet
  const connectToOkxSolana = async () => {
    if (!window.okxwallet || !window.okxwallet.solana) {
      throw new Error("OKX Solana provider not available");
    }
    
    try {
      const provider = window.okxwallet.solana;
      
      // First check if already connected
      if (provider.isConnected && provider.publicKey) {
        const publicKey = provider.publicKey.toString ? 
          provider.publicKey.toString() : 
          provider.publicKey;
        console.log("Already connected to OKX Solana:", publicKey);
        return { publicKey };
      }
      
      console.log("Attempting to connect to OKX Solana...");
      
      // Set up connection timeout (15 seconds)
      const connectionPromise = provider.connect();
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error("Connection timed out after 15 seconds")), 15000)
      );
      
      // Race the connection against the timeout
      const response = await Promise.race([connectionPromise, timeoutPromise]);
      console.log("OKX Solana connection response:", response);
      
      // Handle null response (common with some wallet versions)
      if (response === null) {
        // Check if publicKey is available on the provider after connection
        if (provider.publicKey) {
          const publicKey = provider.publicKey.toString ? 
            provider.publicKey.toString() : 
            provider.publicKey;
          console.log("Found publicKey on provider after null response:", publicKey);
          return { publicKey };
        }
        
        // Wait briefly and check again - some wallets update provider after a slight delay
        await new Promise(resolve => setTimeout(resolve, 500));
        
        if (provider.publicKey) {
          const publicKey = provider.publicKey.toString ? 
            provider.publicKey.toString() : 
            provider.publicKey;
          console.log("Found publicKey on provider after delay:", publicKey);
          return { publicKey };
        }
        
        throw new Error("Could not retrieve public key after connection");
      }
      
      // Handle string response - some wallet versions might return address directly
      if (typeof response === 'string' && response.length > 30) {
        console.log("Received string address from connect:", response);
        return { publicKey: response };
      }
      
      // Handle standard object response with publicKey property
      if (response && response.publicKey) {
        const publicKey = response.publicKey.toString ? 
          response.publicKey.toString() : 
          response.publicKey;
        console.log("Got publicKey from response object:", publicKey);
        return { publicKey };
      }
      
      // Try to find publicKey in nested properties
      if (response && typeof response === 'object') {
        // Check common nested paths
        if (response.data?.publicKey) {
          const publicKey = response.data.publicKey.toString ?
            response.data.publicKey.toString() :
            response.data.publicKey;
          console.log("Found publicKey in response.data:", publicKey);
          return { publicKey };
        }
        
        if (response.result?.publicKey) {
          const publicKey = response.result.publicKey.toString ?
            response.result.publicKey.toString() :
            response.result.publicKey;
          console.log("Found publicKey in response.result:", publicKey);
          return { publicKey };
        }
        
        // Try toString on the response itself
        if (typeof response.toString === 'function') {
          try {
            const strValue = response.toString();
            if (strValue && strValue.length > 30 && strValue !== '[object Object]') {
              console.log("Using toString value as publicKey:", strValue);
              return { publicKey: strValue };
            }
          } catch (e) {
            // Ignore toString errors
          }
        }
      }
      
      // As a final fallback, check the provider again
      if (provider.publicKey) {
        const publicKey = provider.publicKey.toString ? 
          provider.publicKey.toString() : 
          provider.publicKey;
        console.log("Using provider.publicKey as last resort:", publicKey);
        return { publicKey };
      }
      
      throw new Error("Could not find public key in connection response");
    } catch (error) {
      console.error("OKX Solana connection error:", error);
      
      // Handle user rejection
      if (error.code === 4001) {
        throw new Error("User rejected the connection request");
      }
      
      throw error;
    }
  };
  
  // Connect to wallet function with enhanced error handling
  const handleWalletConnect = async (type, preferredChain = null) => {
    console.log('handleWalletConnect called with:', { type, preferredChain });
    
    // Don't allow connection if already connecting
    if (isConnecting) return;
    
    // Check if it's OKX wallet but no chain preference specified
    if (type.toLowerCase() === 'okx' && !preferredChain && !showOkxChainSelect) {
      setWalletType(type);
      setShowOkxChainSelect(true);
      return;
    }
    
    setWalletType(type);
    setIsConnecting(true);
    setError(null);
    setShowChainSupportInfo(true);
    
    // Show chain support info for this wallet type
    const walletSupportInfo = getWalletSupportInfo(type);
    setSupportInfo(walletSupportInfo);
    
    try {
      // Special handling for OKX with Solana chain
      if (type.toLowerCase() === 'okx' && preferredChain === 'solana') {
        // Check if OKX wallet extension exists
        if (!window.okxwallet) {
          setIsConnecting(false);
          setError('OKX Wallet extension not found. Please install OKX Wallet and reload the page.');
          return null;
        }
        
        // Check if Solana provider exists in OKX wallet
        if (!window.okxwallet.solana) {
          setIsConnecting(false);
          setOkxInstallationGuide(true);
          return null;
        }
        
        try {
          // Connect using our enhanced direct method
          const { publicKey } = await connectToOkxSolana();
          
          if (!publicKey) {
            throw new Error("Could not retrieve Solana public key");
          }
          
          console.log("Successfully connected to OKX Solana with key:", publicKey);
          
          // Now call connectWallet to continue the process
          const result = await connectWallet(type, 'solana');
          
          if (result) {
            // Filter check for network compatibility
            if (networkFilter === 'ethereum' || networkFilter === 'bsc') {
              setError(`The connected wallet is using a Solana address. Please change the network filter or connect an EVM wallet.`);
              setIsConnecting(false);
              return null;
            }
            
            // Success!
            onSuccess(result);
            setTimeout(() => onClose(), 500);
            return result;
          } else {
            throw new Error('Failed to connect wallet through WalletContext');
          }
        } catch (err) {
          console.error('OKX Solana connection error:', err);
          
          // Handle specific error codes from OKX documentation
          if (err.code === 4001 || (err.message && err.message.includes('rejected'))) {
            throw new Error('Please approve the connection request in your OKX wallet');
          }
          
          // Rethrow other errors to be caught by the outer catch block
          throw err;
        }
      } else if (type.toLowerCase() === 'phantom') {
        // Special handling for Phantom wallet errors
        try {
          // Check if Phantom is properly installed
          if (!window.phantom?.solana) {
            setError('Phantom wallet not detected. Please install Phantom wallet extension and reload the page.');
            setIsConnecting(false);
            return null;
          }
          
          const result = await connectWallet(type, preferredChain);
          
          if (result) {
            // Check network compatibility
            if (networkFilter !== 'all' && networkFilter !== 'solana') {
              setError(`The connected Phantom wallet is using a Solana address. Please change the network filter or connect an EVM wallet.`);
              setIsConnecting(false);
              return null;
            }
            
            // Success!
            onSuccess(result);
            setTimeout(() => onClose(), 500);
            return result;
          } else {
            throw new Error('Failed to connect Phantom wallet');
          }
        } catch (err) {
          console.error('Phantom connection error:', err);
          
          // Handle Phantom-specific errors
          if (err.code === 4001) {
            throw new Error('Connection request was rejected by user');
          } else if (err.message && err.message.includes('already connected')) {
            throw new Error('Wallet is already connected. Please refresh the page if you experience issues.');
          } else {
            throw new Error(`Failed to connect Phantom wallet: ${err.message}`);
          }
        }
      } else {
        // Standard path for other wallets or OKX with EVM
        const result = await connectWallet(type, preferredChain);
        
        if (result) {
          // Check if wallet address matches the network filter
          if (networkFilter !== 'all') {
            const isAddressSolana = isSolanaWallet(result.address);
            
            if (networkFilter === 'solana' && !isAddressSolana) {
              setError(`The connected ${type} wallet is using an Ethereum address. Please switch to a Solana address in your wallet or change the network filter.`);
              setIsConnecting(false);
              return null;
            }
            
            if ((networkFilter === 'ethereum' || networkFilter === 'bsc') && isAddressSolana) {
              setError(`The connected ${type} wallet is using a Solana address. Please switch to an Ethereum/BSC address in your wallet or change the network filter.`);
              setIsConnecting(false);
              return null;
            }
          }
          
          // Successful connection
          onSuccess(result);
          
          // Close modal with small delay for better UX
          setTimeout(() => {
            onClose();
          }, 500);
          
          return result;
        } else {
          throw new Error('Failed to connect wallet');
        }
      }
    } catch (err) {
      console.error(`Failed to connect ${type} wallet:`, err);
      
      // Special error handling for OKX Solana errors
      if (type.toLowerCase() === 'okx' && preferredChain === 'solana') {
        if (err.message && (
          err.message.includes('User rejected') || 
          err.message.includes('not authorized') || 
          err.message.includes('not approved') ||
          err.message.includes('approve the connection request')
        )) {
          setError('Please approve the connection request in your OKX wallet');
        } else if (err.message && (
          err.message.includes('not found') || 
          err.message.includes('not available') ||
          err.message.includes('Could not retrieve') ||
          err.message.includes('Provider does not implement') ||
          err.message.includes('No viable connection method') ||
          err.message.includes('cannot read properties') ||
          err.message.includes('is not a function') ||
          err.message.includes('null') ||
          err.message.includes('undefined') ||
          err.message.includes('timeout') ||
          err.message.includes('Could not find public key')
        )) {
          setOkxInstallationGuide(true);
          setError(null);
        } else {
          setError(`Connection failed: ${err.message || 'Unknown error'}`);
        }
      } 
      // Special handling for Phantom wallet errors
      else if (type.toLowerCase() === 'phantom') {
        if (err.message && err.message.includes('rejected')) {
          setError('Connection request was rejected. Please approve the connection in your Phantom wallet.');
        } else if (err.message && err.message.includes('Unsupported path')) {
          setError('Phantom wallet connection failed. Please make sure you have created a Solana account in Phantom wallet.');
        } else if (err.message && (err.message.includes('not found') || err.message.includes('not detected'))) {
          setError('Phantom wallet not detected. Please install the Phantom wallet extension and reload the page.');
        } else {
          setError(`Failed to connect Phantom wallet: ${err.message || 'Unknown error'}`);
        }
      }
      else {
        setError(`Connection failed: ${err.message || 'Unknown error'}`);
      }
      
      return null;
    } finally {
      setIsConnecting(false);
    }
  };
  
  // Filter wallets based on selected network
  const filteredWallets = () => {
    if (networkFilter === 'all') return Object.keys(availableWallets).filter(w => availableWallets[w]);
    
    return Object.keys(availableWallets).filter(w => {
      const supportInfo = getWalletSupportInfo(w);
      if (networkFilter === 'ethereum' || networkFilter === 'bsc') {
        return availableWallets[w] && supportInfo.supportsEVM;
      } else if (networkFilter === 'solana') {
        return availableWallets[w] && supportInfo.supportsSolana;
      }
      return availableWallets[w];
    });
  };
  
  if (!isOpen) return null;
  
  // Render OKX installation guide if needed
  if (okxInstallationGuide) {
    return (
      <div className="wallet-modal-overlay" aria-modal="true" role="dialog">
        <div className="wallet-modal" ref={modalRef}>
          <button 
            className="wallet-modal-close" 
            onClick={onClose} 
            aria-label="Close"
          >×</button>
          <OkxInstallationGuide 
            onTryAgain={() => {
              setOkxInstallationGuide(false);
              if (walletType) {
                handleWalletConnect(walletType, 'solana');
              }
            }}
            onCancel={() => setOkxInstallationGuide(false)}
          />
        </div>
      </div>
    );
  }
  
  // Render OKX chain selection dialog if needed
  if (showOkxChainSelect) {
    return (
      <div className="wallet-modal-overlay" aria-modal="true" role="dialog">
        <div className="wallet-modal" ref={modalRef}>
          <button 
            className="wallet-modal-close" 
            onClick={onClose} 
            aria-label="Close"
          >×</button>
          <OkxChainSelection
            onSelectEthereum={() => {
              setShowOkxChainSelect(false);
              setChainPreference('ethereum');
              handleWalletConnect('okx', 'ethereum');
            }}
            onSelectSolana={() => {
              setShowOkxChainSelect(false);
              setChainPreference('solana');
              handleWalletConnect('okx', 'solana');
            }}
            onCancel={() => {
              setShowOkxChainSelect(false);
              setWalletType(null);
            }}
          />
        </div>
      </div>
    );
  }
  
  // Main wallet connection modal
  return (
    <div className="wallet-modal-overlay" aria-modal="true" role="dialog">
      <div className="wallet-modal" ref={modalRef}>
        <button 
          className="wallet-modal-close" 
          onClick={onClose} 
          aria-label="Close"
          disabled={isConnecting}
        >
          ×
        </button>
        
        <div className="wallet-modal-header">
          <h2>Connect Your Wallet</h2>
          <p>Select a wallet to connect to the AeroFi network</p>
          
          {/* Network filter tabs */}
          <div className="network-filter-tabs">
            <button 
              className={`network-tab ${networkFilter === 'all' ? 'active' : ''}`}
              onClick={() => setNetworkFilter('all')}
            >
              All Networks
            </button>
            <button 
              className={`network-tab ${networkFilter === 'ethereum' ? 'active' : ''}`}
              onClick={() => setNetworkFilter('ethereum')}
            >
              Ethereum
            </button>
            <button 
              className={`network-tab ${networkFilter === 'bsc' ? 'active' : ''}`}
              onClick={() => setNetworkFilter('bsc')}
            >
              BSC
            </button>
            <button 
              className={`network-tab ${networkFilter === 'solana' ? 'active' : ''}`}
              onClick={() => setNetworkFilter('solana')}
            >
              Solana
            </button>
          </div>
        </div>
        
        {/* Chain support info */}
        {showChainSupportInfo && (
          <div className="chain-support-info">
            <div className={`support-info-box ${supportInfo.supportsEVM && supportInfo.supportsSolana ? 'dual' : (supportInfo.supportsSolana ? 'solana' : 'evm')}`}>
              <div className="support-info-icon">
                {supportInfo.supportsEVM && supportInfo.supportsSolana ? (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                    <path d="M8 12L11 15L16 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                ) : (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                    <path d="M12 8V16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    <path d="M12 19V19.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                )}
              </div>
              <div className="support-info-text">
                <p>{supportInfo.supportInfo}</p>
                {networkFilter !== 'all' && (
                  <p className="support-warning">
                    {networkFilter === 'ethereum' && !supportInfo.supportsEVM && 
                      "Warning: You've selected Ethereum filter, but this wallet only supports Solana!"
                    }
                    {networkFilter === 'bsc' && !supportInfo.supportsEVM && 
                      "Warning: You've selected BSC filter, but this wallet only supports Solana!"
                    }
                    {networkFilter === 'solana' && !supportInfo.supportsSolana && 
                      "Warning: You've selected Solana filter, but this wallet only supports EVM chains!"
                    }
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
        
        <div className="wallet-options">
          {filteredWallets().length === 0 ? (
            <div className="no-wallets-message">
              <p>No compatible wallets found for the selected network. Please install a supported wallet or choose a different network.</p>
            </div>
          ) : (
            filteredWallets().map((wallet) => (
              <button 
                key={wallet}
                className={`wallet-option ${walletType === wallet ? 'connecting' : ''}`}
                onClick={() => handleWalletConnect(wallet)}
                disabled={isConnecting}
                aria-label={`Connect ${wallet.charAt(0).toUpperCase() + wallet.slice(1)} Wallet`}
              >
                {getWalletIcon(wallet)}
                <div className="wallet-info">
                  <h3>{wallet.charAt(0).toUpperCase() + wallet.slice(1)} Wallet</h3>
                  <div className="wallet-networks">
                    {getWalletNetworks(wallet).map((network, i) => (
                      <span key={network} className={`network-badge ${network}`}>
                        {network.charAt(0).toUpperCase() + network.slice(1)}
                      </span>
                    ))}
                  </div>
                </div>
                {getNetworkSupportBadge(wallet)}
                {isConnecting && walletType === wallet && (
                  <div className="connecting-indicator">
                    <span className="spinner"></span>
                  </div>
                )}
              </button>
            ))
          )}
        </div>
        
        {error && (
          <div className="wallet-error" role="alert">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10" stroke="#ff5252" strokeWidth="2"/>
              <path d="M12 8V12" stroke="#ff5252" strokeWidth="2" strokeLinecap="round"/>
              <path d="M12 16H12.01" stroke="#ff5252" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            <p>{error}</p>
          </div>
        )}
        
        <div className="wallet-footer">
          <p>By connecting your wallet, you agree to our <a href="https://docs.aeronyx.network/articles/aledger-platform-master-terms-of-service" target="_blank" rel="noopener noreferrer">Terms of Service</a> and <a href="https://docs.aeronyx.network/articles/aledger-platform-privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a></p>
          
          {isMobile && (
            <div className="mobile-wallet-tips">
              <p>Don't see your wallet? Make sure you're browsing with your wallet's built-in browser, or install a compatible wallet app.</p>
              <div className="wallet-app-links">
                {filteredWallets().slice(0, 2).map(wallet => (
                  <a 
                    key={wallet}
                    href={`https://${wallet}.io`} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="wallet-app-link"
                  >
                    <span>Get {wallet.charAt(0).toUpperCase() + wallet.slice(1)} Wallet</span>
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WalletConnector;
